import { useId, useState, type FormEvent } from "react";
import type { Expense, ExpenseLinksInput } from "@/api/finance";
import { DriveLinkField, type DriveUploader } from "@/components/forms/DriveLinkField";
import { RequestError } from "@/components/RequestError";
import { FormActions } from "@/components/ui/form-actions";
import { useFormValues, useSubmitState } from "@/lib/use-form";

type ExpenseLinksFormProps = {
  expense: Expense;
  /** Attach the bill or proof to the company Drive; without it the links are typed only. */
  drive?: DriveUploader;
  onSubmit: (input: ExpenseLinksInput) => Promise<unknown>;
  onCancel: () => void;
  onReload?: () => void;
};

/**
 * The bill and the payment proof of a saved expense, added, changed or cleared. The bill is
 * filed under the year of the expense; the proof under the year it was paid in.
 */
export function ExpenseLinksForm({
  expense,
  drive,
  onSubmit,
  onCancel,
  onReload,
}: ExpenseLinksFormProps) {
  const id = useId();
  const { values, set } = useFormValues({ bill_url: expense.bill_url, proof_url: expense.proof_url });
  const { error, errors, saving, run } = useSubmitState();
  const [uploads, setUploads] = useState(0);
  const countUpload = (busy: boolean): void => setUploads((n) => n + (busy ? 1 : -1));
  const fileLabel = expense.payee || expense.description;

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    void run(() =>
      onSubmit({ bill_url: values.bill_url.trim(), proof_url: values.proof_url.trim() }),
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
      <RequestError error={error} onReload={onReload} />
      <div className="grid gap-4 md:grid-cols-2">
        <DriveLinkField
          id={`${id}-bill`}
          label="Bill link"
          kind="bill"
          on={expense.spent_on}
          fileLabel={fileLabel}
          value={values.bill_url}
          onChange={(value) => set("bill_url", value)}
          error={errors.bill_url}
          hint="Optional: a link to the bill in the company Drive (https); empty removes it"
          drive={drive}
          onBusy={countUpload}
        />
        <DriveLinkField
          id={`${id}-proof`}
          label="Proof link"
          kind="proof"
          on={expense.paid_on ?? expense.spent_on}
          fileLabel={fileLabel}
          value={values.proof_url}
          onChange={(value) => set("proof_url", value)}
          error={errors.proof_url}
          hint="Optional: a link to the proof in the company Drive (https); empty removes it"
          drive={drive}
          onBusy={countUpload}
        />
      </div>
      <FormActions
        saving={saving}
        submitLabel="Save links"
        onCancel={onCancel}
        disabled={uploads > 0}
      />
    </form>
  );
}
